import { useEffect, useState } from 'react'
import {
  currentPatientAsync,
  resetCurrentPatient,
  undoVisitAsync,
  resetCurrentPatientStatus,
  resetNotesListAndNoteById,
} from '../../../redux/flows/portal/currentPatient'
import { resetCallInNextVisitStatus, setCurrentVisit } from '../../../redux/flows/portal/lobby'
import { useAppDispatch, useAppSelector } from '../../../redux/store'
import { APIStatus } from '../../../redux/types'

export const useCurrentPatient = () => {
  const dispatch = useAppDispatch()
  const currentVisit = useAppSelector((state) => state.lobbyPortal.currentVisit)
  const currentPatient = useAppSelector((state) => state.currentPatientPortal.currentPatient)
  const queue = useAppSelector((state) => state.lobbyPortal.queue)
  const sessionStatus = useAppSelector((state) => state.currentPatientPortal.sessionStatus)
  const notesUploadUrl = useAppSelector((state) => state.currentPatientPortal.notesUploadUrl)
  const startChiropracticStatus = useAppSelector((state) => state.currentPatientPortal.startChiropracticStatus)
  const stopChiropracticStatus = useAppSelector((state) => state.currentPatientPortal.stopChiropracticStatus)
  const callInNextVisitStatus = useAppSelector((state) => state.lobbyPortal.callInNextVisitStatus)
  const currentPatientStatus = useAppSelector((state) => state.currentPatientPortal.currentPatientStatus)
  const currentPatientError = useAppSelector((state) => state.currentPatientPortal.currentPatientError)
  const undoVisitError = useAppSelector((state) => state.currentPatientPortal.undoVisitError)
  const undoVisitStatus = useAppSelector((state) => state.currentPatientPortal.undoVisitStatus)

  const [retryAttemptCount, setRetryAttemptCount] = useState(0)
  const [undoAttemptCount, setUndoAttemptCount] = useState(0)

  const handleRetry = () => {
    if (currentVisit?.visitId && retryAttemptCount < 2) {
      setRetryAttemptCount((prev) => prev + 1)
      dispatch(currentPatientAsync(currentVisit.visitId))
    }
  }

  const handleUndoVisit = () => {
    if (currentVisit?.visitId && undoAttemptCount < 2) {
      setUndoAttemptCount((prev) => prev + 1)
      dispatch(undoVisitAsync({ visitId: currentVisit.visitId }))
    }
  }

  // Check if retry should be disabled (both retries failed)
  const isRetryDisabled = retryAttemptCount >= 2 && currentPatientStatus === APIStatus.rejected

  // Handle failure after both retry and undo have failed twice
  useEffect(() => {
    const shouldReset =
      retryAttemptCount >= 2 &&
      undoAttemptCount >= 2 &&
      currentPatientStatus === APIStatus.rejected &&
      undoVisitStatus === APIStatus.rejected

    if (shouldReset) {
      dispatch(resetCurrentPatientStatus())
      dispatch(resetCurrentPatient())
      dispatch(setCurrentVisit(null))
      setRetryAttemptCount(0)
      setUndoAttemptCount(0)
    }
  }, [retryAttemptCount, undoAttemptCount, currentPatientStatus, undoVisitStatus, dispatch])

  // Reset attempts on success
  useEffect(() => {
    if (currentPatientStatus === APIStatus.fulfilled) {
      setRetryAttemptCount(0)
      setUndoAttemptCount(0)
    }
  }, [currentPatientStatus])

  useEffect(() => {
    if (undoVisitStatus === APIStatus.fulfilled) {
      setRetryAttemptCount(0)
      setUndoAttemptCount(0)
    }
  }, [undoVisitStatus])

  // Clear everything when undo visit is successful from model
  useEffect(() => {
    if (undoAttemptCount > 0 && undoVisitStatus === APIStatus.fulfilled) {
      dispatch(resetCurrentPatientStatus())
      dispatch(setCurrentVisit(null))
    }
  }, [undoAttemptCount, undoVisitStatus, dispatch])

  useEffect(() => {
    if (callInNextVisitStatus === APIStatus.fulfilled && currentVisit?.visitId) {
      dispatch(currentPatientAsync(currentVisit.visitId))
    }
  }, [callInNextVisitStatus])

  useEffect(() => {
    return () => {
      dispatch(resetCallInNextVisitStatus())
    }
  }, [])

  useEffect(() => {
    if (currentVisit === null) {
      dispatch(resetCurrentPatient())
      setRetryAttemptCount(0)
      setUndoAttemptCount(0)
    }
  }, [currentVisit, dispatch])

  // Reset notes list and note by id when current patient is changed
  useEffect(() => {
    if (currentPatient) {
      dispatch(resetNotesListAndNoteById())
    }
  }, [currentPatient])

  return {
    currentVisit,
    currentPatient,
    queue,
    sessionStatus,
    notesUploadUrl,
    startChiropracticStatus,
    stopChiropracticStatus,
    currentPatientStatus,
    currentPatientError,
    undoVisitError,
    handleRetry,
    handleUndoVisit,
    isRetryDisabled,
  }
}

export default useCurrentPatient
